import {
  CopilotAction, CopilotState, RUN_CAP_HINT, SessionState, SseEvent, TERMINAL_PHASES, TimelineItem, TRACE_MARKER,
} from './types'
import { toActivity } from '../util/activity'

let itemSeq = 0
const nextItemId = () => `ti_${itemSeq += 1}`

export const initialState: CopilotState = {
  open: false, activeId: null, sessions: {}, order: [],
}

export function newSessionState(clientId: string): SessionState {
  return {
    clientId,
    sessionId: null,
    title: 'New session',
    phase: 'empty',
    timeline: [],
    interruptRequested: false,
    unread: false,
  }
}

export function aggregateUnread(state: CopilotState): number {
  return state.order.filter((id) => state.sessions[id]?.unread).length
}

/** Update one session slice, marking it unread when it is not the active one. */
function patch(state: CopilotState, clientId: string, fn: (s: SessionState) => SessionState): CopilotState {
  const current = state.sessions[clientId]
  if (!current) return state
  let next = fn(current)
  if (clientId !== state.activeId) next = { ...next, unread: true }
  return { ...state, sessions: { ...state.sessions, [clientId]: next } }
}

function applySse(s: SessionState, event: SseEvent, at: number): SessionState {
  switch (event.event) {
    case 'status': {
      const phase = s.phase === 'creating' || s.phase === 'queued' ? 'streaming' : s.phase
      const item: TimelineItem = {
        id: nextItemId(), kind: 'status', at, text: event.data.text,
      }
      return { ...s, phase, timeline: [...s.timeline, item] }
    }
    case 'tool_use': {
      // Defensive: a frame without a string `summary` (or with no data) must not throw at render.
      const data = event.data ?? { summary: '' }
      const activity = toActivity(data)
      const summary = typeof data.summary === 'string' ? data.summary : ''
      // A Read never gets a `tool_result`, so its row is done the moment it appears. Left `running`, it
      // would absorb the next call's result (the first running row closes), and every later step would
      // show the result of the one before it — OQ-1, which removing the Grep/Glob frames made the norm.
      const status = activity?.type === 'read' ? 'ok' : 'running'
      const row: TimelineItem = {
        id: nextItemId(), kind: 'tool', at, summary, status,
      }
      const item: TimelineItem = activity ? { ...row, activity } : row
      const phase = s.phase === 'creating' || s.phase === 'queued' ? 'streaming' : s.phase
      return { ...s, phase, timeline: [...s.timeline, item] }
    }
    case 'tool_result': {
      // STATUS ONLY. The row closed here is the first running one, and a Read never gets a result of
      // its own, so it may be a Read: copying this frame's fields onto it would relabel it as the call.
      const idx = s.timeline.findIndex((t) => t.kind === 'tool' && t.status === 'running')
      if (idx === -1) return s
      const timeline = s.timeline.slice()
      timeline[idx] = { ...(timeline[idx] as Extract<TimelineItem, { kind: 'tool' }>), status: event.data.ok ? 'ok' : 'failed' }
      return { ...s, timeline }
    }
    case 'tool_call_confirmation':
      return { ...s, phase: 'awaiting_confirmation', pendingConfirmation: event.data }
    case 'question': {
      const terminal = event.data.text.toLowerCase().includes(RUN_CAP_HINT)
      return { ...s, phase: terminal ? 'complete' : 'awaiting_answer', pendingQuestion: { text: event.data.text, terminal } }
    }
    case 'confidence':
      return { ...s, confidence: event.data }
    case 'session_complete':
      return {
        ...s,
        phase: 'complete',
        result: event.data,
      }
    case 'error':
      if (s.interruptRequested) {
        const item: TimelineItem = {
          id: nextItemId(), kind: 'error', at, message: 'Run cancelled.', cancelled: true,
        }
        return { ...s, phase: 'interrupted', timeline: [...s.timeline, item] }
      }
      return {
        ...s,
        phase: 'error',
        error: event.data.message,
        timeline: [...s.timeline, {
          id: nextItemId(), kind: 'error', at, message: event.data.message, cancelled: false,
        }],
      }
    default:
      return s
  }
}

export function reducer(state: CopilotState, action: CopilotAction): CopilotState {
  switch (action.type) {
    case 'OPEN': return { ...state, open: true }
    case 'CLOSE': return { ...state, open: false }
    case 'NEW_SESSION':
      return {
        ...state,
        open: true,
        activeId: action.clientId,
        order: [...state.order, action.clientId],
        sessions: { ...state.sessions, [action.clientId]: newSessionState(action.clientId) },
      }
    case 'SET_ACTIVE':
      return patch({ ...state, activeId: action.clientId }, action.clientId, (s) => ({ ...s, unread: false }))
    case 'UPLOAD_START':
      return patch(state, action.clientId, (s) => ({ ...s, phase: 'uploading', title: action.title }))
    case 'SESSION_CREATING':
      return patch(state, action.clientId, (s) => ({ ...s, phase: 'creating' }))
    case 'SESSION_CREATED':
      return patch(state, action.clientId, (s) => ({ ...s, sessionId: action.sessionId }))
    case 'MESSAGE_TRACED':
      return patch(state, action.clientId, (s) => {
        // Insert at the head of the current work segment, not at the end: the POST response can
        // land after the first SSE frames, and a `tool` item appended behind early `status` items
        // would make splitTimeline treat them as activity instead of the answer.
        const cut = s.timeline.map((i) => i.kind).lastIndexOf('user') + 1
        const marker: TimelineItem = {
          id: nextItemId(),
          kind: 'tool',
          at: 0,
          status: 'ok',
          summary: `${TRACE_MARKER}${action.traceId ?? 'n/a'} · session: ${s.sessionId ?? 'n/a'}`,
        }
        return { ...s, timeline: [...s.timeline.slice(0, cut), marker, ...s.timeline.slice(cut)] }
      })
    case 'MESSAGE_SENT':
      return patch(state, action.clientId, (s) => {
        if (TERMINAL_PHASES.includes(s.phase) || s.phase === 'awaiting_answer') {
          return {
            ...s, phase: 'streaming', result: undefined, error: undefined, pendingQuestion: undefined,
          }
        }
        return {
          ...s,
          phase: s.phase === 'creating' || s.phase === 'empty' ? 'queued' : s.phase,
          pendingQuestion: undefined,
        }
      })
    case 'CONFIRM_SENT':
      return patch(state, action.clientId, (s) => ({ ...s, phase: 'streaming', pendingConfirmation: undefined }))
    case 'INTERRUPT_REQUESTED':
      return patch(state, action.clientId, (s) => ({ ...s, interruptRequested: true }))
    case 'USER_TURN':
      return patch(state, action.clientId, (s) => ({
        ...s,
        timeline: [...s.timeline, {
          id: nextItemId(), kind: 'user', at: action.at, text: action.text, files: action.files,
        }],
      }))
    case 'SSE':
      return patch(state, action.clientId, (s) => applySse(s, action.event, action.at ?? 0))
    default:
      return state
  }
}
